import React, {useCallback} from 'react';
import {RouteComponentProps} from 'react-router-dom';

import Promotion from '../../components/Promotion';
import {ButtonProps} from '../../components/Button';
import Description from '../../components/Description';
import PaymentLayout from '../../layouts/PaymentLayout';
import PackageItem, {PriceReducedReason} from '../../components/PackageItem';
import {ReactComponent as Logo} from '../../assets/image/mana.svg';

import {useEffectOnce} from 'react-use';
import {useTranslation} from 'react-i18next';
import {useDispatchCtx} from '../../context';

import {
  AppState,
  PackageWithPromotion,
  setChosenPackage,
  setMessage,
} from '../../state-management/app';
import getKnowNative, {MIXPANEL} from '../../externals/mobile-communication';

import {appendQuery} from '../../utils';
import {PROMOTION_STATUS} from '../../constants/enum';

import styles from './PaymentPackage.module.css';


interface PaymentUpgradeProps extends AppState, RouteComponentProps {
  onConfirm: (rawPromotionCode: string) => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}


const PaymentPackage = (props: PaymentUpgradeProps) => {
  const dispatch = useDispatchCtx();
  const {t} = useTranslation();

  const {
    packages,
    chosenPackage,
    activeOrder,
    history,
    location,
    promotion
  } = props;

  useEffectOnce(() => {
    getKnowNative('package');
  });


  const onClickPackage = useCallback((pack: PackageWithPromotion) => {
    getKnowNative('mixpanel', undefined, {
      type: MIXPANEL.PAYMENT_SELECT_PACKAGE,
      packageId: pack.id,
      packageName: pack.name
    });

    dispatch(setChosenPackage(pack));
  }, [dispatch]);

  const onNavigate = () => {
    if (!chosenPackage) return dispatch(setMessage('pleaseChoosePackage'));

    return history.push('/detail?' + appendQuery(location.search, 'packageId', String(chosenPackage.id)));
  };

  const buttonProps: ButtonProps = {
    disabled: !chosenPackage || !!(promotion.text && promotion.status === PROMOTION_STATUS.INVALID) || promotion.status === PROMOTION_STATUS.LOADING, //disable if user did not choose package , or chosenPackage is greyOut or user enter promotion but status is invalid
    onClick: onNavigate,
    children: t('goToPayment')
  };

  return (
    <PaymentLayout buttonProps={buttonProps} shouldShowButton={packages.length !== 0}>
      <div className={styles.root}>
        {
          packages.length > 0 ?
            <div className={styles.packages}>
              <div className="mn-grid">
                {
                  packages.map((pack) => {
                    const priceReduceReasons: PriceReducedReason[] = [
                      {
                        isDiscounted: !!pack.carriedOverAmount,
                        reason: activeOrder ? activeOrder.planName : '',
                        reducedAmount: pack.carriedOverAmount || 0,
                        text: t('discountPriceSince')
                      },
                      {
                        isDiscounted: !!pack.isPromotionApplied && promotion.status === PROMOTION_STATUS.VALID,
                        reason: promotion.text,
                        reducedAmount: pack.promotionDiscountValue || 0,
                        text: t('reducedBy')
                      }
                    ];

                    return (
                      <PackageItem
                        key={pack.id}
                        pack={pack}
                        recommended={pack.isRecommended}
                        isGreyOut={pack.isGreyOut}
                        selected={chosenPackage ? chosenPackage.id === pack.id : false}
                        priceReduceReasons={priceReduceReasons}
                        onClick={onClickPackage}
                      />
                    )
                  })
                }
              </div>
              <div className={styles.promotion}>
                <Description title={t('promotion')}>
                  <Promotion
                    promotion={promotion}
                    onChange={props.onChange}
                    onConfirm={props.onConfirm}
                    onClick={() => getKnowNative('inputFocus')}
                  />
                </Description>
              </div>
            </div> :
            <div className={styles.sorryContainer}>
              <div className={styles.sorry}>{t('noPlanAvailable')}.</div>
              <Logo/>
            </div>
        }
      </div>
    </PaymentLayout>
  );
};

export default PaymentPackage;
